import React, { useCallback, useMemo, useState, useEffect } from 'react';
import styles from './form-profile-edit.module.scss';
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form';
import { Avatar, BottomSheet, Button, DataPicker, Input, InputRedesigned, TargetModal, Textarea, Typography, Uploader } from '@/ui-kit';
import { FieldBlock } from '../field-block';
import { profileEditSchema, ProfileEditSchemaType } from '@/schemes/profile-edit-schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { MyProfile, Photo, Profile } from '@/types/profile';
import debounce from 'lodash/debounce';
import { usePhotoUpload } from '@/hooks/use-photo-upload';
import { useDeletePhotoById } from '@/hooks/use-delete-photo-by-id';
import { HeaderMenu } from '../header-menu';
import { useUpdateUserData } from '@/hooks/use-update-profile';
import { renderCity } from '@/utils/render-city';
import WebApp from '@twa-dev/sdk';
import { LocationProfile } from '@/components/popup-block/location-profile';
import { SelectOption } from '@/ui-kit/select';
import { eventsApi, profilesApi } from '@/api';
import { ANY_REGION_OPTION, ANY_REGION_OPTION_LABEL } from '@/utils/regions';
import { ArrowBackUpIcon, ChatIcon, CheckIcon, DropdownIndicator, ErrorNotificationIcon, FaceVisibleIcon, HeartIcon, HelpIcon, ModerationPendingIcon, PeopleIcon, PurposeNotSpecifiedIcon, RealPhotoIcon, SuccessNotificationIcon } from '@/assets/icons';
import { usePhotoUpdate } from '@/hooks/use-photo-update';
import { callYaMetrika } from '@/utils/call-ya-metrika';
import { routesApp } from '@/constans/routes';
import { Link } from 'react-router-dom';
import { useToast } from '@/store/useToast';
import clsx from 'clsx';
import { EditPhotoIcon } from '@/assets/icons';
import { ProfileActiveSection } from '../profile-active-section/profile-active-section';
import { useAuth } from '@/store/useAuth';
import { format } from 'date-fns';
import { RadioCupertino } from '@/ui-kit/radio-cupertino';
import photosStimulProfileEditSignup2Icon from '@/assets/icons/photos-stimul-signup-2.0-icon.png'
import { VerificationBottomsheet } from '../verification-bottomsheet';
import { SelfInitiatedVerificationBottomsheet } from '../self-initiated-verification-bottomsheet';
import { Trans, useTranslation } from 'react-i18next';
import { localizeDate } from '@/utils/msgs';

interface Props {
    userData: MyProfile;
}

const DELAY_SEND_DATA = 1000;

export const FormProfileEdit: React.FC<Props> = ({ userData }) => {
    const showToast = useToast((state) => state.show);

    let firstName = '';
    if (WebApp.initDataUnsafe.user) {
        firstName = WebApp.initDataUnsafe.user.first_name;
    }

    const { t } = useTranslation();

    const defaultValues = useMemo(() => {
        return {
            public_name: userData?.public_name ?? firstName,
            about_me: userData?.about_me ?? null,
            travel_plans: userData?.travel_plans ?? null,
            purpose: userData?.purpose,
            region: userData?.region ? { label: renderCity(userData.region), value: userData.region.id } : undefined,
            regions: userData?.regions
                ? userData.regions.map((region) => ({
                      label: renderCity(region),
                      value: region.id,
                  }))
                : [
                    ANY_REGION_OPTION
                ],
            dob: userData?.dob ?? '',
            gender: userData?.gender,
        };
    }, [userData]);

    const [isPurposeSelected, setIsPurposeSelected] = useState<Profile['purpose']>(defaultValues.purpose);
    const [progress, setProgress] = useState(0);
    const [popupEditLocation, setPopupEditLocation] = useState(false);
    const { mutateAsync: updateUserData } = useUpdateUserData();
    const methods = useForm<ProfileEditSchemaType>({
        resolver: zodResolver(profileEditSchema),
        defaultValues,
    });
    const { mutateAsync: uploadPhoto } = usePhotoUpload();
    const { mutateAsync: deletePhotoById } = useDeletePhotoById();
    const { mutateAsync: updatePhoto } = usePhotoUpdate();

    const isPhotoUploading = !!progress;

    const photoCount = useMemo(() => {
        return userData?.photos?.length ?? 0;
    }, [userData?.photos?.length]);

    const [isEditLoading, setIsEditLoading] = useState(false);
    const [isDeleteLoading, setIsDeleteLoading] = useState(false);
    const [isTargetEditting, setTargetEditting] = useState(false);

    const setUserData = useAuth((state) => state.setProfile);

    const handleUploadPhoto = async (file: File | null) => {
        if (!file) return;

        try {
            const formData = new FormData();
            formData.append('photo[image]', file);
            const { data } = await uploadPhoto({ dataPhoto: formData, callback: setProgress });

            handleUpdateUserData({ ...userData, photos: [...(userData?.photos ?? []), data] });

            collectEditPhotoEvent();
        } finally {
            setProgress(0);
        }
    };

    const handleUpdatePhoto = async (photoId: number, file: File | null) => {
        if (!file || isEditLoading) return;

        setIsEditLoading(true)
        try {
            const formData = new FormData();
            formData.append('photo[image]', file);
            const { data } = await updatePhoto({ id: photoId, dataPhoto: formData, callback: setProgress });

            const photos = [
                ...userData?.photos
            ]
            photos[
                userData
                    .photos
                    .findIndex(
                        (e: Photo) =>
                            e.id === photoId
                    )
            ] = data

            handleUpdateUserData({ ...userData, photos });

            console.log(`update photos: ${photos.map(e => e.thumb_url)}`)

            // collectEditPhotoEvent();
        } finally {
            setProgress(0);
            setIsEditLoading(false)
        }
    };

    const handleDeletePhoto = async (photoId?: number) => {
        if (!photoId || photoCount === 1 || isDeleteLoading) return;

        setIsDeleteLoading(true);

        try {
            const res = await deletePhotoById(photoId);
            if (res.status === 200) {
                handleUpdateUserData({
                    ...userData,
                    photos: userData?.photos?.filter((photo) => photo.id !== photoId),
                });

                collectEditPhotoEvent();
            }
        } catch (error) {
            console.error(`Error in ${handleDeletePhoto.name}:`, error);
        } finally {
            setIsDeleteLoading(false);
        }
    };

    const {
        register,
        reset,
        formState: { errors, isValid, isDirty, dirtyFields, },
    } = methods;

    const handleChangePurpose = (value: Profile['purpose']) => {
        setIsPurposeSelected(value);
    };

    const collectEditEvent = (field: string) => eventsApi.updateUserEvent(`profile_${field}`);

    const collectEditPhotoEvent = () => collectEditEvent('photo');

    const handleUpdateUserData = async (data: any, needToast: boolean = false, onSuccess = () => {}) => {
        console.log('handleUpdateUserData')
        if (!userData) return;

        try {
            //NProgress.start();
            await updateUserData({
                ...data,
                region_ids: data.regions.map((item: { id: any; value: any }) => item.value || item.id),
            });

            if (needToast) {
                showToast({
                    icon: <SuccessNotificationIcon />,
                    msg: t('profile.saved')
                });
            }
            onSuccess()
        } catch {
            reset();
            showToast({
                icon: <ErrorNotificationIcon />,
                msg: t('error_try_again')
            });
        } finally {
            //NProgress.done();
        }
    };

    const handleUpdateUserDataDebounce = useCallback(debounce(handleUpdateUserData, DELAY_SEND_DATA), []);

    const collectEditEventDebounce = useCallback(debounce(collectEditEvent, DELAY_SEND_DATA), []);

    //methods.watch(['public_name', 'about_me', 'travel_plans', 'regions']);

    useEffect(() => {
        const subscription = methods.watch(async (data, { name, type }) => {
            if (
                !name ||
                type !== 'change'
            ) return;

            const isValid = await methods.trigger(name);
            if (
                !isValid ||
                name === 'public_name' ||
                name === 'gender'
            ) return;

            console.log('useEffect')
            handleUpdateUserDataDebounce(data, true);
            collectEditEventDebounce(name);
        });

        return () => subscription.unsubscribe();
    }, [methods, handleUpdateUserDataDebounce, collectEditEventDebounce]);

    const locationHandler = async (regions: SelectOption[]) => {
        setPopupEditLocation(false);
        // @ts-ignore
        methods.setValue('regions', regions.map(
            (e: SelectOption) => e.value)
            .includes(
                ANY_REGION_OPTION.value
            )
            ? []
            : regions, { shouldValidate: true, shouldDirty: true }
        );

        await methods.trigger('regions');
        handleUpdateUserDataDebounce(methods.getValues(), true);
        collectEditEventDebounce('regions');
    };

    const onMyFormPreviewClick = () => {
        callYaMetrika('my_form_preview');
    };

    const onNameControlClick = () => {
        setIsEditNameModalOpen(true)
    }

    const [isEditNameModalOpen, setIsEditNameModalOpen] = useState(false)
    const [isNameChangeable, setNameChangeable] = useState(false)
    const NAME_WAS_CHANGED_KEY = 'NAME_WAS_CHANGED'
    
    useEffect(() => {
        setNameChangeable(
            localStorage
                .getItem(NAME_WAS_CHANGED_KEY) !== 'true'
        )
    }, [])
    
    const [isEditDateModalOpen, setIsEditDateModalOpen] = useState(false);
    const [tempDate, setTempDate] = useState<string | null>(null)

    const GENDER_WAS_CHANGED_KEY = 'GENDER_WAS_CHANGED'
    const [isGenderChangeable, setGenderChangeable] = useState(false)
    const [isEditGenderModalOpen, setIsEditGenderModalOpen] = useState(false);

    useEffect(() => {
        setGenderChangeable(
            localStorage
                .getItem(GENDER_WAS_CHANGED_KEY) !== 'true'
        )
    }, [])

    const [isShowPhotosInfoModal, setIsShowPhotosInfoModal] = useState(false);

    const scrollToField = (control: string) => {
        window
            .scrollTo({
                top: (
                    document
                        .getElementById(`${control}_field`)
                        ?.offsetTop ?? 0
                ) -
                WebApp
                    .safeAreaInset
                    .top -
                (
                    document
                        .querySelector('nav')
                        ?.getBoundingClientRect()
                        .height ?? 0
                )
            });
    }

    const DISPLAY_FIELDS: Record<string, string> = {
        public_name: t('profile.fill_name'),
        gender: t('profile.fill_gender'),
        purpose: t('profile.fill_purpose'),
        travel_plans: t('profile.fill_travel_plans'),
        dob: t('profile.fill_dob'),
        photos: t('profile.fill_photos'),
        regions: t('profile.fill_regions'),
        about_me: t('profile.fill_about_me'),
    }

    const getHalfDoneProgress = () => {
        const total = Object.values(userData.requirements);
        const valid = total.filter(Boolean);
        const ratio = valid.length / total.length;
        return ratio.toLocaleString('en', { style: 'percent' });
    };

    const [isVerificationModalOpen, setIsVerificationModalOpen] =
        useState(false);
    
    const onVerificationBottomsheetOkClicked = async () => {
        try {
            await profilesApi.selfInitiateVerification()
            setUserData({ ...userData, verification_status: 'self_initiated' })
            return true
        } catch (e) {
            showToast({
                icon: <ErrorNotificationIcon />,
                msg: t('error_try_again')
            });
        }
        return false
    }

    if (popupEditLocation) {
        return (
            <LocationProfile
                regions={userData.regions}
                onSubmit={locationHandler}
                onClose={() => setPopupEditLocation(false)}
            />
        );
    }
    
    return (
        <div className={styles.wrapper}>
            <FormProvider {...methods}>
                <form className={styles.form}>
                    <HeaderMenu
                        title={t('profile.questionnaire')}
                        headerBg="gray"
                        isFixed />

                    {
                        (
                            userData
                                ?.moderation_status
                                === 'change_required' &&
                            userData
                                .violations_resolved
                                === false
                        ) &&
                        <div className={styles.remoderationCard}>
                            <Typography
                                fontWeight="semibold"
                                className={styles.remoderationCardTitle}>
                                <Trans i18nKey="please_edit_your_details" />
                            </Typography>
                            <Typography className={styles.remoderationCardSubitle}>
                                {t('passing_verification_note')}
                            </Typography>
                            <ul className={styles.remoderationCardRequirementsList}>
                                {
                                    (userData.violations ?? [])
                                        .map(e => (
                                            <li className={styles.remoderationCardRequirementsListItem}>
                                                <CheckIcon className={
                                                        clsx(
                                                            styles.remoderationCardRequirementsListItemIcon,
                                                            e.processed &&
                                                            styles.remoderationCardRequirementsListItemIconChecked
                                                        )
                                                    } />
                                                <Typography>
                                                    {e.name}
                                                </Typography>
                                                <ArrowBackUpIcon
                                                    className={styles.remoderationCardRequirementsListItemArrow}
                                                    onClick={() => {
                                                        window
                                                            .scrollTo({
                                                                top: (
                                                                    document
                                                                        .getElementById(`${e.field_name}_field`)
                                                                        ?.offsetTop ?? 0
                                                                ) -
                                                                WebApp
                                                                    .safeAreaInset
                                                                    .top -
                                                                (
                                                                    document
                                                                        .querySelector('nav')
                                                                        ?.getBoundingClientRect()
                                                                        .height ?? 0
                                                                )
                                                            });
                                                    }} />
                                            </li>
                                        ))
                                }
                            </ul>
                            <div className={styles.remoderationCardDivider} />
                            <Typography
                                className={styles.remoderationCardLink}
                                center
                                onClick={() => WebApp.openTelegramLink('https://t.me/euphora_support')}>
                                {t('profile.need_support')}
                            </Typography>
                        </div>
                    }

                    {
                        userData?.state === 'half_done' &&
                        <div className={styles.finishFillYourselfDataCard}>
                            <Typography
                                fontWeight="semibold"
                                className={styles.finishFillYourselfDataCardTitle}>
                                {t('profile.finish_fill_yourself_data_title')}
                            </Typography>
                            <Typography className={styles.finishFillYourselfDataCardSubitle}>
                                {t('profile.finish_fill_yourself_data_subtitle')}
                            </Typography>
                            <div className={styles.finishFillYourselfDataCardProgress}>
                                <button
                                    style={{
                                        width: getHalfDoneProgress()
                                    }}
                                    className={styles.finishFillYourselfDataCardProgressThumb}>
                                    {
                                        getHalfDoneProgress()
                                    }
                                </button>
                            </div>
                            <ul className={styles.finishFillYourselfDataCardRequirementsList}>
                                {
                                    Object.entries(userData.requirements ?? {})
                                        .map(e => (
                                            <li className={styles.finishFillYourselfDataCardRequirementsListItem}>
                                                <CheckIcon className={
                                                        clsx(
                                                            styles.finishFillYourselfDataCardRequirementsListItemIcon,
                                                            e[1] &&
                                                            styles.finishFillYourselfDataCardRequirementsListItemIconChecked
                                                        )
                                                    } />
                                                <Typography typeColor="gray">
                                                    {
                                                        DISPLAY_FIELDS[e[0]]
                                                    }
                                                </Typography>
                                                <ArrowBackUpIcon
                                                    className={styles.finishFillYourselfDataCardRequirementsListItemArrow}
                                                    onClick={() => scrollToField(e[0])} />
                                            </li>
                                        ))
                                }
                            </ul>
                        </div>
                    }

                    {
                        (
                            userData
                                ?.moderation_status
                                === 'change_required' &&
                            userData
                                .violations_resolved
                                === true
                        ) &&
                        <div className={styles.moderationPendingCard}>
                            <ModerationPendingIcon
                                width={40}
                                height={40}
                                className={styles.moderationPendingCardIcon} />
                            <div className={styles.moderationPendingCardContent}>
                                <Typography
                                    fontWeight="semibold"
                                    className={styles.moderationPendingCardContentTitle}>
                                    {t('your_profile_is_being_reviewed')}
                                </Typography>
                                <Typography>
                                    {t('we_are_currently_checking_your_info')}
                                </Typography>
                            </div>
                        </div>
                    }

                    <div className={styles.sectionTitle}>
                        <div id="photos_field">
                            <Typography
                                variant="p"
                                fontWeight="semibold"
                                fontFamily="SFProText"
                                className={styles.label}>
                                {t('profile.my_photos')}
                            </Typography>
                        </div>
                        <HelpIcon
                            width={16}
                            height={16}
                            onClick={() => setIsShowPhotosInfoModal(true)} />
                    </div>
                    
                    <BottomSheet
                        isOpen={isShowPhotosInfoModal}
                        isDeskorTop={false}
                        className={styles.bottomsheet}
                        onClose={() => setIsShowPhotosInfoModal(false)}>
                        <div className={styles.photosInfoModalContent}>
                            <div className={styles.photosInfoModalContentHeaderAvatarWrapper}>
                                <Avatar
                                    size={140}
                                    src={userData.photos.length ? userData.photos[0].main_url : null} />
                            </div>
                            <Typography
                                variant={'h4'}
                                fontWeight={'bold'}
                                className={styles.photosInfoModalContentHeaderTitle}>
                                {t('profile.make_sure_photo_exactly_meets_requirements')}
                            </Typography>
                            <Typography className={styles.photosInfoModalContentHeaderSubtitle} variant={'h6'}>
                                {t('profile.we_will_check_photo_according_to_several_criteria')}
                            </Typography>
                            <ul className={styles.photosInfoModalContentRequirements}>
                                <li className={styles.photosInfoModalContentRequirement}>
                                    <FaceVisibleIcon width={24} height={24} />
                                    <Typography typeColor="gray-label" variant="h6">
                                        {t('profile.face_must_be_clearly_visible')}
                                    </Typography>
                                </li>
                                <li className={styles.photosInfoModalContentRequirement}>
                                    <PeopleIcon width={24} height={24} />
                                    <Typography typeColor="gray-label" variant="h6">
                                        {t('profile.you_need_to_be_alone_in_the_photo')}
                                    </Typography>
                                </li>
                                <li className={styles.photosInfoModalContentRequirement}>
                                    <RealPhotoIcon width={24} height={24} />
                                    <Typography typeColor="gray-label" variant="h6">
                                        {t('profile.photo_must_be_real')}
                                    </Typography>
                                </li>
                            </ul>
                            <div className={styles.photosInfoModalContentFooter}>
                                <Button
                                    typeColor="orange"
                                    className={styles.photosInfoModalContentFooterBtn}
                                    onClick={() => setIsShowPhotosInfoModal(false)}>
                                    {t('profile.ok_secondary')}
                                </Button>
                            </div>
                        </div>
                    </BottomSheet>
                    
                    <div
                        className={styles.uploaderBlok}>
                        {userData.photos?.map((photo, index) => (
                            <Uploader
                                key={photo.id}
                                photoUrl={photo.thumb_url}
                                isCanDelete={
                                    index > 0 ||
                                    userData.photos.length >= 2
                                }
                                isActive={index === 0}
                                isUploading={isPhotoUploading && index === 0 && isEditLoading}
                                progress={progress}
                                isEditLoading={isEditLoading}
                                isDeleteLoading={isDeleteLoading}
                                photoCount={photoCount}
                                className={
                                    clsx(
                                        styles.uploader,
                                        styles.profileEditUploader,
                                        userData.requirements?.photos === false &&
                                        styles.unfilledField
                                    )
                                }
                                onChange={async (e) => await handleUpdatePhoto(userData?.photos[index]?.id, e)}
                                onDeletePhoto={() => handleDeletePhoto(userData?.photos[index]?.id)} />
                        ))}

                        {Array.from({ length: 6 - userData.photos?.length }).map((_, index) => {
                            const isActive = !index;

                            return (
                                <Uploader
                                    key={Math.random()}
                                    isActive={isActive}
                                    isUploading={isPhotoUploading && isActive && !isEditLoading}
                                    progress={progress}
                                    className={
                                        clsx(
                                            styles.uploader,
                                            styles.profileEditUploader,
                                            userData.requirements?.photos === false &&
                                            styles.unfilledField
                                        )
                                    }
                                    onChange={handleUploadPhoto} />
                            );
                        })}
                    </div>

                    {
                        userData.photos.length === 0 &&
                        <div className={styles.note}>
                            <img
                                className={styles.noteIcon}
                                src={photosStimulProfileEditSignup2Icon}
                                width="48px"
                                height="48px" />
                            
                            <div className={styles.noteAside}>
                                <Typography
                                    variant="p"
                                    fontWeight="semibold"
                                    className={styles.noteAsideTitle}>
                                    {t('photos_note_title')}
                                </Typography>
                                <Typography
                                    variant="p"
                                    fontWeight="medium"
                                    className={styles.noteAsideSubtitle}>
                                    {t('photos_note_subtitle')}
                                </Typography>
                            </div>
                        </div>
                    }

                    <Link
                        to={routesApp.myFormPreview}
                        className={
                            clsx(
                                styles.myFormPreviewCard,
                                userData.state === 'half_done' && styles.unfilledFieldViewContainer,
                            )
                        }
                        onClick={onMyFormPreviewClick}>
                        <Typography
                            variant="p"
                            fontWeight="normal"
                            fontFamily="SFProText"
                            className={
                                clsx(
                                    styles.cardTitle,
                                    userData.state === 'half_done' && styles.unfilledFieldView,
                                )
                            }>
                            {t('profile.view_your_profile')}
                        </Typography>
                        <ArrowBackUpIcon
                            className={
                                clsx(
                                    styles.myFormPreviewCardIcon,
                                    userData.state === 'half_done' && styles.unfilledFieldView,
                                )
                            }
                            width={18}
                            height={18} />
                    </Link>

                    {
                        userData.verification_status === 'unverified' &&
                        <div className={styles.formCardsBlock} id="verification">
                            <div className={styles.editProfileFormContainerTarget}>
                                {
                                    <div
                                        className={
                                            clsx(
                                                styles.purposeUnfilledCard,
                                                styles.unfilledField
                                            )
                                        }>
                                        <Typography
                                            variant="span"
                                            fontWeight="semibold"
                                            fontFamily="SFProText"
                                            className={
                                                clsx(
                                                    styles.label,
                                                    styles.purposeUnfilledCardTitle
                                                )
                                            }>
                                            {t('profile.start_verification_secondary')}
                                        </Typography>
                                        <Typography className={styles.purposeUnfilledCardSubtitle}>
                                            {t('profile.verification_increases_your_chances')}
                                        </Typography>
                                        <Button
                                            typeColor="orange"
                                            type="button"
                                            className={styles.verificationBtn}
                                            onClick={() => setIsVerificationModalOpen(true)}>
                                            <CheckIcon className={styles.verificationIcon} />
                                            <Typography typeColor="white">
                                                {t('start_verification')}
                                            </Typography>
                                        </Button>
                                    </div>
                                }
                            </div>
                            <SelfInitiatedVerificationBottomsheet
                                isVerificationModalOpen={isVerificationModalOpen}
                                setIsVerificationModalOpen={setIsVerificationModalOpen}
                                onOkClicked={onVerificationBottomsheetOkClicked} />
                        </div>
                    }
                    
                    <Typography
                        variant="p"
                        fontWeight="semibold"
                        fontFamily="SFProText"
                        className={styles.sectionTitle}>
                        {t('profile.main_section')}
                    </Typography>
                    
                    {
                        isNameChangeable &&
                        <div
                            className={
                                clsx(
                                    styles.nameControl,
                                    styles.mb4
                                )
                            }
                            id="public_name_field"
                            onClick={onNameControlClick}>
                            <Typography
                                variant="p"
                                fontWeight="normal"
                                fontFamily="SFProText"
                                className={
                                    clsx(
                                        styles.cardLabel,
                                        styles.cardTitle
                                    )
                                }>
                                {t('name')}
                            </Typography>
                            <Typography
                                variant="p"
                                fontWeight="normal"
                                fontFamily="SFProText"
                                className={styles.cardLabel}>
                                {
                                    defaultValues.public_name ?? ''
                                }
                            </Typography>
                            <ArrowBackUpIcon
                                className={styles.myFormPreviewCardIcon}
                                width={20}
                                height={20} />
                        </div>
                    }

                    <BottomSheet
                        isOpen={isEditNameModalOpen}
                        isDeskorTop={true}
                        className={styles.bottomsheet}
                        onClose={() =>
                            setIsEditNameModalOpen(false)
                        }
                        onCloseFinished={() =>
                            methods.resetField('public_name')
                        }>
                        <div className={styles.editNameModalContent}>
                            <div className={styles.editNameModalContentHeader}>
                                <Typography
                                    className={styles.editNameModalContentHeaderTitle}
                                    variant={'h5'}
                                    fontWeight={'semibold'}>
                                    {t('profile.your_name_secondary')}
                                </Typography>
                            </div>
                            <div className={styles.editNameModalContentMain}>
                                <div className={styles.editNameModalContentMainInput}>
                                    <InputRedesigned
                                        placeholder={t('profile.your_name_secondary')}
                                        defaultValue={defaultValues.public_name ?? ''}
                                        error={errors?.public_name?.message}
                                        {...register('public_name', { required: true })} />
                                    <Typography
                                        className={styles.editDateModalContentNote}
                                        variant={'p'}>
                                        {t('profile.name_can_be_changed_only_once')}
                                    </Typography>
                                </div>
                                {!!errors?.public_name?.message}
                                <div className={styles.editDateModalContentFooter}>
                                    <Button
                                        disabled={
                                            !!errors?.public_name?.message ||
                                            !dirtyFields.public_name
                                        }
                                        typeColor="orange"
                                        className={styles.editNameModalContentFooterBtn}
                                        onClick={() => {
                                            handleUpdateUserData(
                                                {
                                                    ...userData,
                                                    public_name: methods.getValues('public_name')
                                                },
                                                true,
                                                () => {
                                                    localStorage
                                                        .setItem(NAME_WAS_CHANGED_KEY, 'true')
                                                    setNameChangeable(false)
                                                }
                                            );
                                            collectEditEvent('public_name')
                                            setIsEditNameModalOpen(false)
                                        }}>
                                        {t('done')}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </BottomSheet>
                    
                    <button
                        className={
                            clsx(
                                'react-datepicker__button',
                                userData.requirements?.dob === false &&
                                styles.unfilledField
                            )
                        }
                        type="button"
                        id="dob_field"
                        onClick={() => setIsEditDateModalOpen(true)}>
                        <Typography
                            fontWeight="normal"
                            fontFamily="SFProText"
                            className="react-datepicker__button-text">
                            {t('dob_title')}
                        </Typography>
                        <div className="react-datepicker__button-label">
                            <Typography
                                fontWeight="normal"
                                variant="span"
                                typeColor="gray"
                                className="react-datepicker__button-text">
                                {
                                    userData?.dob ?
                                        localizeDate(userData.dob)
                                    :
                                        t('profile.not_specified')
                                }
                            </Typography>{' '}
                            <DropdownIndicator />
                        </div>
                    </button>
                    
                    <BottomSheet
                        isOpen={isEditDateModalOpen}
                        isDeskorTop={false}
                        className={styles.bottomsheet}
                        onClose={() => {
                            setIsEditDateModalOpen(false)
                            setTempDate(null)
                        }}>
                        <Controller
                            control={methods.control}
                            name={'dob'}
                            render={({ field: { onChange, value, ref } }) => {
                                return (
                                    <div className={styles.editDateModalContent}>
                                        <div className={styles.editDateModalContentHeader}>
                                            <Typography
                                                className={styles.editDateModalContentHeaderTitle}
                                                variant={'h5'}
                                                fontWeight={'bold'}>
                                                {t('dob_title')}
                                            </Typography>
                                        </div>
                                        <DataPicker
                                            className={styles.datePicker}
                                            dob={methods.getValues('dob')}
                                            isRequireChanges={false}
                                            onChangeDate={setTempDate} />
                                        <div className={styles.editDateModalContentFooter}>
                                            <Button
                                                typeColor="orange"
                                                className={styles.editDateModalContentFooterBtn}
                                                onClick={() => {
                                                    if (tempDate) {
                                                        onChange(tempDate)
                                                        collectEditEvent('dob')
                                                        setIsEditDateModalOpen(false)
                                                        setTempDate(null)
                                                    }
                                                }}>
                                                {t('done')}
                                            </Button>
                                        </div>
                                    </div>
                                )
                            }} />
                    </BottomSheet>

                    {
                        isGenderChangeable &&
                        <button
                            className={
                                clsx(
                                    'react-datepicker__button',
                                )
                            }
                            type="button"
                            id="gender_field"
                            onClick={async () => setIsEditGenderModalOpen(true)}>
                            <Typography fontFamily="SFProText" className="react-datepicker__button-text">
                                {t('profile.gender')}
                            </Typography>
                            <div className="react-datepicker__button-label">
                                <Typography
                                    variant="span"
                                    typeColor="gray"
                                    className="react-datepicker__button-text">
                                    {
                                        userData?.gender === 'male' ?
                                            t('male')
                                        : userData?.gender === 'female' ?
                                            t('female')
                                        :
                                            t('profile.not_specified')
                                    }
                                </Typography>{' '}
                                <DropdownIndicator />
                            </div>
                        </button>
                    }

                    <BottomSheet
                        isOpen={isEditGenderModalOpen}
                        isDeskorTop={true}
                        className={styles.bottomsheet}
                        onClose={() => {
                            setIsEditGenderModalOpen(false)
                            methods.resetField('gender')
                        }}>
                        <Controller
                            control={methods.control}
                            name={'gender'}
                            render={({ field: { onChange } }) => {
                                return (
                                    <div className={styles.editGenderModalContent}>
                                        <div className={styles.editGenderModalContentHeader}>
                                            <Typography
                                                className={styles.editGenderModalContentHeaderTitle}
                                                variant={'h5'}
                                                fontWeight={'bold'}>
                                                {t('your_gender_secondary')}
                                            </Typography>
                                        </div>
                                        <div className={styles.editGenderModalContentMain}>
                                            <div className={styles.editGenderModalContentMainInput}>
                                                <div className={styles.genderWrapper}>
                                                    <div
                                                        className={
                                                            clsx(
                                                                styles.genderBtn,
                                                                methods
                                                                    .getValues('gender') === 'female'
                                                                    && styles.genderBtnActive
                                                            )
                                                        }>
                                                        <RadioCupertino
                                                            label={t('i_female')}
                                                            value="female"
                                                            {...register('gender')} />
                                                    </div>

                                                    <div
                                                        className={
                                                            clsx(
                                                                styles.genderBtn,
                                                                methods
                                                                    .getValues('gender') === 'male'
                                                                    && styles.genderBtnActive
                                                            )
                                                        }>
                                                        <RadioCupertino
                                                            label={t('i_male')}
                                                            value="male"
                                                            {...register('gender')} />
                                                    </div>
                                                </div>
                                                {
                                                    <Typography className={styles.editGenderModalContentNote} variant={'p'}>
                                                        {t('profile.gender_can_only_be_changed_once')}
                                                    </Typography>
                                                }
                                            </div>
                                            <div className={styles.editGenderModalContentFooter}>
                                                <Button
                                                    typeColor="orange"
                                                    className={styles.editGenderModalContentFooterBtn}
                                                    disabled={
                                                        !!errors?.gender?.message ||
                                                        !dirtyFields.gender
                                                    }
                                                    onClick={() => {
                                                        collectEditEvent('gender')
                                                        handleUpdateUserData(
                                                            {
                                                                ...userData,
                                                                gender: methods.getValues('gender')
                                                            },
                                                            true,
                                                            () => {
                                                                localStorage
                                                                    .setItem(GENDER_WAS_CHANGED_KEY, 'true')
                                                                setGenderChangeable(false)
                                                            }
                                                        )
                                                        setIsEditGenderModalOpen(false)
                                                    }}>
                                                    {t('done')}
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                )
                            }} />
                    </BottomSheet>

                    <FieldBlock
                        id="about_me_field"
                        error={!!errors?.about_me?.message}>
                        <Textarea
                            label={t('about_secondary')}
                            maxLength={255}
                            placeholder={t('about_me_placeholder')}
                            defaultValue={defaultValues.about_me ?? ''}
                            className={styles.textarea}
                            {...register('about_me', { required: true })}
                        />
                    </FieldBlock>
                    
                    <div className={styles.formLabel} id="purpose_field">
                        <Typography
                            variant="p"
                            fontWeight="semibold"
                            fontFamily="SFProText"
                            className={styles.label}>
                            {t('purpose')}
                        </Typography>
                    </div>

                    <div className={styles.formCardsBlock}>
                        <div
                            className={styles.editProfileFormContainerTarget}
                            onClick={() => setTargetEditting(true)}>
                            {
                                isPurposeSelected === 'fun' ?
                                    <div
                                        className={clsx(
                                            styles.formContainerTargetItem,
                                            isPurposeSelected === 'fun' && styles.formContainerTargetItemActive,
                                        )}
                                        onClick={() => handleChangePurpose('fun')}>
                                        <div className={styles.formContainerTargetItemPhoto}>
                                            <ChatIcon className={styles.newFriendsIcon} />
                                        </div>
                                        <div className={styles.formContainerTargetItemText}>
                                            <h3>{t('fun')}</h3>
                                            <p>{t('fun_desc')}</p>
                                        </div>

                                        {isPurposeSelected === 'fun' && (
                                            <EditPhotoIcon
                                                width={34}
                                                height={34} />
                                        )}
                                    </div>
                                : isPurposeSelected === 'love' ?
                                    <div
                                        className={clsx(
                                            styles.formContainerTargetItem,
                                            isPurposeSelected === 'love' && styles.formContainerTargetItemActive,
                                        )}
                                        onClick={() => handleChangePurpose('love')}>
                                        <div className={styles.formContainerTargetItemPhoto}>
                                            <HeartIcon className={styles.romanticIcon} />
                                        </div>
                                        <div className={styles.formContainerTargetItemText}>
                                            <h3>{t('love')}</h3>
                                            <p>{t('love_desc')}</p>
                                        </div>

                                        {isPurposeSelected === 'love' && (
                                            <EditPhotoIcon width={34} height={34} />
                                        )}
                                    </div>
                                : isPurposeSelected === 'not_specified' ?
                                    <div
                                        className={clsx(
                                            styles.formContainerTargetItem,
                                            isPurposeSelected === 'not_specified' && styles.formContainerTargetItemActive,
                                        )}
                                        onClick={() => handleChangePurpose('not_specified')}>
                                        <div className={styles.formContainerTargetItemPhoto}>
                                            <PurposeNotSpecifiedIcon className={styles.howGoesIcon} />
                                        </div>
                                        <div className={styles.formContainerTargetItemText}>
                                            <h3>{t('not_specified')}</h3>
                                            <p>{t('not_specified_desc')}</p>
                                        </div>

                                        {isPurposeSelected === 'not_specified' && (
                                            <EditPhotoIcon
                                                width={34}
                                                height={34} />
                                        )}
                                    </div>
                                :
                                    <div
                                        className={
                                            clsx(
                                                styles.purposeUnfilledCard,
                                                userData.requirements?.purpose === false &&
                                                styles.unfilledField
                                            )
                                        }>
                                        <Typography
                                            variant="p"
                                            fontWeight="semibold"
                                            fontFamily="SFProText"
                                            className={
                                                clsx(
                                                    styles.label,
                                                    styles.purposeUnfilledCardTitle
                                                )
                                            }>
                                            {t('profile.relationships_communication_or_friendship')}
                                        </Typography>
                                        <Typography className={styles.purposeUnfilledCardSubtitle}>
                                            {t('profile.what_you_are_looking_for')}
                                        </Typography>
                                        <Button
                                            typeColor="orange"
                                            type="button"
                                            className={styles.editDateModalContentFooterBtn}
                                            onClick={() => setTargetEditting(true)}>
                                            {t('profile.specify_target')}
                                        </Button>
                                    </div>
                            }
                        </div>
                        <TargetModal
                            isOpen={isTargetEditting}
                            onClose={() => setTargetEditting(false)}
                            isDeskorTop={true}
                            title={t('what_are_you_here_for')}>
                            {userData && (
                                <>
                                    <div className={styles.formContainerTarget}>
                                        <div
                                            className={clsx(
                                                styles.formContainerTargetItem,
                                                isPurposeSelected === 'fun' && styles.formContainerTargetItemActive,
                                            )}
                                            onClick={() => handleChangePurpose('fun')}>
                                            <div className={styles.formContainerTargetItemPhoto}>
                                                <ChatIcon className={styles.newFriendsIcon} />
                                            </div>
                                            <div className={styles.formContainerTargetItemText}>
                                                <h3>{t('fun')}</h3>
                                                <p>{t('fun_desc')}</p>
                                            </div>

                                            {isPurposeSelected === 'fun' && (
                                                <CheckIcon
                                                    width={20}
                                                    height={20}
                                                    className={styles.iconTop} />
                                            )}
                                        </div>

                                        <div
                                            className={clsx(
                                                styles.formContainerTargetItem,
                                                isPurposeSelected === 'love' && styles.formContainerTargetItemActive,
                                            )}
                                            onClick={() => handleChangePurpose('love')}>
                                            <div className={styles.formContainerTargetItemPhoto}>
                                                <HeartIcon className={styles.romanticIcon} />
                                            </div>
                                            <div className={styles.formContainerTargetItemText}>
                                                <h3>{t('love')}</h3>
                                                <p>{t('love_desc')}</p>
                                            </div>

                                            {isPurposeSelected === 'love' && (
                                                <CheckIcon
                                                    width={20}
                                                    height={20}
                                                    className={styles.iconTop} />
                                            )}
                                        </div>

                                        <div
                                            className={clsx(
                                                styles.formContainerTargetItem,
                                                isPurposeSelected === 'not_specified' && styles.formContainerTargetItemActive,
                                            )}
                                            onClick={() => handleChangePurpose('not_specified')}>
                                            <div className={styles.formContainerTargetItemPhoto}>
                                                <PurposeNotSpecifiedIcon className={styles.howGoesIcon} />
                                            </div>
                                            <div className={styles.formContainerTargetItemText}>
                                                <h3>{t('not_specified')}</h3>
                                                <p>{t('not_specified_desc')}</p>
                                            </div>

                                            {isPurposeSelected === 'not_specified' && (
                                                <CheckIcon
                                                    width={20}
                                                    height={20}
                                                    className={styles.iconTop} />
                                            )}
                                        </div>
                                    </div>
                                    <Controller
                                        name="purpose"
                                        control={methods.control}
                                        render={({ field: { onChange } }) => (
                                            <Button
                                                typeColor="orange"
                                                variant="stretched"
                                                className={styles.saveBtn}
                                                onClick={() => {
                                                    onChange(isPurposeSelected!);
                                                    collectEditEvent('purpose');
                                                    setTargetEditting(false);
                                                }}>
                                                {t('done')}
                                            </Button>
                                        )} />
                                </>
                            )}
                        </TargetModal>
                    </div>

                    <FieldBlock
                        id="travel_plans_field"
                        error={!!errors?.travel_plans?.message}>
                        <Textarea
                            label={t('travel_plans_label')}
                            maxLength={500}
                            placeholder={t('travel_plans_placeholder')}
                            defaultValue={defaultValues.travel_plans ?? ''}
                            {...register('travel_plans', { required: true })} />
                    </FieldBlock>
                    
                    <div
                        id="regions_field"
                        className={
                            clsx(
                                styles.combobox,
                                styles.mb6,
                                userData.requirements?.regions === false &&
                                styles.unfilledField
                            )
                        }
                        onClick={() => setPopupEditLocation(true)}>
                        <Typography
                            variant="label"
                            fontWeight="normal"
                            className={
                                clsx(
                                    styles.required,
                                    styles.cardTitle,
                                    styles.comboboxTitle,
                                )
                            }>
                            {t('city_or_country_of_search')}
                        </Typography>
                        <Typography
                            variant="label"
                            fontWeight="normal"
                            typeColor="gray"
                            className={
                                clsx(
                                    styles.required,
                                    styles.cardSubtitle
                                )
                            }>
                            {defaultValues.regions.length ? (
                                defaultValues.regions.map((item, index) => <span key={index}>{item.label}</span>)
                            ) : (
                                <span>{t(ANY_REGION_OPTION_LABEL)}</span>
                            )}

                        </Typography>
                        <DropdownIndicator />
                    </div>

                    <Typography
                        variant="span"
                        fontWeight="semibold"
                        fontFamily="SFProText"
                        className={styles.sectionTitle}>
                        {t('profile.additional')}
                    </Typography>

                    <ProfileActiveSection />
                </form>
            </FormProvider>
        </div>
    );
};
